/**
 * Source catalog. `ro.ts` is typed against this object, so a missing or extra key fails the
 * typecheck (ADR 0008). Functions are for interpolation and plurals.
 */
import { enAccounts } from './en-accounts.js';

const DIFFICULTY = ['easy', 'medium', 'hard'] as const;

export const en = {
  app: { name: 'PebbleCode' },
  nav: {
    exercises: 'Exercises',
    myProjects: 'My projects',
    joinSession: 'Join a session',
    home: 'Home',
    progress: 'My progress',
  },
  lang: { label: 'Interface language' },
  theme: { toLight: 'Switch to light theme', toDark: 'Switch to dark theme' },
  common: {
    close: 'Close',
    cancel: 'Cancel',
    loading: 'Loading…',
    retry: 'Try again',
    copy: 'Copy',
    copied: 'Copied',
    notFound: 'Not found',
    somethingWrong: 'Something went wrong.',
    goHome: 'Back to the home page',
    breadcrumb: 'Breadcrumb',
    reload: 'Reload the page',
    crashed: 'Something broke on this page. Your blocks are saved — reloading usually fixes it.',
    resizeSide: 'Resize the left panel',
    resizeCode: 'Resize the code panel',
  },
  shortcuts: {
    run: 'Ctrl+Enter',
    step: 'F10',
    stepBack: 'Shift+F10',
    stop: 'Esc',
  },
  top: {
    saved: 'Saved',
    saving: 'Saving…',
    saveFailed: 'Not saved — retrying',
    share: 'Share',
    run: 'Run',
    stop: 'Stop',
    modeBlocks: 'Blocks',
    modeCode: 'Code',
    modeHint: 'Switch between dragging blocks and writing code',
    code: 'Show code',
    hideCode: 'Hide the code',
    freeProject: 'Free project',
    untitled: 'Untitled project',
  },
  category: {
    all: 'All',
    sequences: 'Sequences',
    conditions: 'Conditions',
    loops: 'Loops',
    digits: 'Digits & numbers',
    arrays: 'Vectors and matrices',
    strings: 'Text & characters',
  },
  exercise: {
    position: (i: number, n: number) => `Exercise ${i} of ${n}`,
    difficulty: (d: number) => `Difficulty: ${DIFFICULTY[d - 1] ?? DIFFICULTY[0]}`,
    input: 'Input',
    output: 'Expected output',
    examples: 'Examples used to check your program',
    examplesNote: 'Your program must print exactly this. Press Run to check every example.',
    exampleN: (i: number) => `Example ${i}`,
    yourOutput: 'Your program printed',
    exampleStatus: {
      ok: 'passes',
      'wrong-output': 'different output',
      error: 'stopped with an error',
      'needs-input': 'asked for more input',
      'too-long': 'ran too long',
    },
    blocksYouMightNeed: 'Blocks you might need',
    freeTitle: 'Free project',
    freeText:
      'No task here — build anything you like. Blocks attached under “when program starts” run when you press Run.',
    solved: 'Solved — every example passes',
    done: 'Done',
    inProgress: 'In progress',
  },
  toolbox: {
    logic: 'Logic',
    loops: 'Loops',
    math: 'Math',
    variables: 'Variables',
    text: 'Text',
    io: 'In / Out',
    lists: 'Lists',
  },
  chips: {
    while: 'while',
    repeat: 'repeat',
    for: 'for',
    if: 'if',
    ifElse: 'if / else',
    mod: 'mod',
    intDiv: 'integer ÷',
    read: 'read',
    print: 'print',
    compare: 'compare',
    and: 'and / or',
    change: 'change by',
    join: 'join',
    break: 'break',
    list: 'make a list',
    table: 'make a table',
    index: 'place in a list',
    length: 'length of',
    letter: 'character of a text',
    code: 'ASCII code',
    case: 'capital / small letter',
    find: 'position of',
    part: 'part of a text',
  },
  code: {
    tabsLabel: 'Code language',
    tabs: { cpp: 'C++', pseudo: 'Pseudocod', py: 'Python', js: 'JavaScript' },
    copy: 'Copy code',
    copied: 'Code copied',
    empty: 'Attach blocks under “when program starts” and the code appears here.',
    lineIs: (line: number) => `Line ${line}`,
    highlightedBlock: 'is the highlighted block:',
    selectedBlock: 'is the selected block:',
    readOnlyView: (lang: string) =>
      `You are writing the ${lang} version. This view follows what you type.`,
    mistake: (line: number, what: string) => `Line ${line}: ${what}`,
    parseError: {
      expected: (p: { expected?: string; found?: string }) =>
        p.expected
          ? `${p.expected} was expected here${p.found ? `, not ${p.found}` : ''}.`
          : 'Something is missing here.',
      unsupported: (p: { what?: string }) =>
        `PebbleCode cannot run ${p.what ?? 'this'} yet — it has no block for it.`,
      'no-main': () => 'The program needs int main() { … } around your code.',
      unterminated: (p: { what?: string }) =>
        p.what === 'character'
          ? "This character is never closed — write it like this: 'a'."
          : `This ${p.what ?? 'text'} is never closed.`,
    },
    jsHelpers:
      'In this view, readNumber(), readText() and print() stand for reading a line of input and printing a line.',
    explain: {
      set: (name: string) => `${name} gets a new value.`,
      read: (name: string) => `${name} gets the next value from the Input box.`,
      change: (name: string) => `${name} goes up (or down) by the amount shown.`,
      print: 'The value is written to the output.',
      if: 'The condition is checked; only the matching branch runs.',
      repeat: 'The blocks inside run a fixed number of times.',
      while: 'The condition is checked before every round; the loop stops when it is false.',
      for: (name: string) =>
        `${name} counts from the first number to the last, one step each round.`,
      break: 'The loop stops right here and the program continues after it.',
      newList: (name: string) => `${name} becomes a list, with every place empty.`,
      newTable: (name: string) => `${name} becomes a table, with every place empty.`,
      setAt: (name: string) => `One place of ${name} gets a new value.`,
    },
  },
  run: {
    status: {
      idle: 'Ready',
      running: 'Running',
      stepping: 'Stepping',
      paused: (step: number) => `Paused at step ${step}`,
      finished: (steps: number) => `Finished in ${steps.toLocaleString('en')} steps`,
      stopped: 'Stopped',
      stepLimit: 'Stopped · step limit',
      error: 'Stopped · error',
      waiting: 'Waiting for input',
    },
    input: 'Input',
    inputHint: 'One value per line',
    output: 'Output',
    clear: 'Clear',
    stepBack: 'Step back',
    step: 'Step',
    stop: 'Stop',
    speed: 'Speed',
    speedValue: (v: string) => `Speed: ${v}`,
    instant: 'instant',
    waitingNext: 'waiting for the next step…',
    typeValue: 'Type a value and press Enter',
    readTrace: (value: string) => `read ← ${value}`,
    loopTrace: (k: number, vars: string) => `loop ${k}: ${vars}`,
    askWhy: 'Ask the tutor why',
    noOutput: 'Press Run to see the output here.',
    allPass: 'Every example passes — exercise solved!',
    examplesFail: (passed: number, total: number) => `${passed} of ${total} examples pass.`,
    errors: {
      stepLimit: (n: number) =>
        `Stopped after ${n.toLocaleString('en')} steps — the loop never ends.`,
      divisionByZero: 'Division by zero.',
      notANumber: (value: string) => `“${value}” is not a number.`,
      notAWholeNumber: (value: string) => `“${value}” is not a whole number.`,
      noValue: (name: string) => `${name} has no value yet — set it before using it.`,
      tooLong: 'The text got too long.',
      outOfRange: (p: Record<string, string>) =>
        p.text
          ? Number(p.last) < 0
            ? `${p.name || 'The text'} is empty; you asked for position ${p.index ?? '?'}.`
            : `${p.name || 'The text'} has positions 0 to ${p.last ?? '0'}; you asked for position ${p.index ?? '?'}.`
          : `${p.name ?? 'The list'} has places ${p.first ?? '0'} to ${p.last ?? '0'}; you asked for place ${p.index ?? '?'}.`,
      badSize: 'A list needs a whole number of places, and not more than two million.',
      badCode: (value: string) =>
        `There is no character with code ${value}: ASCII codes go from 0 to 127.`,
      badLength: (value: string) =>
        `A piece of text cannot have ${value} characters — ask for 0 or more.`,
      cantRun: 'Fix the highlighted block first.',
    },
  },
  diag: {
    'no-start': 'Add the “when program starts” block and attach your blocks under it.',
    'extra-start': 'Only the first “when program starts” block runs.',
    'empty-input': 'This block has an empty slot.',
    'unknown-block': 'This block is not supported here.',
    'read-placement': '“read” can only be used in “set … to”.',
    'break-outside-loop': '“break out of loop” must be inside a loop.',
    'type-conflict': (p: Record<string, string>) =>
      `${p.name ?? ''} holds both a number and text — use two variables.`,
    'needs-number': 'This block needs numbers.',
    'needs-whole-number': 'This block needs whole numbers.',
    'needs-text': 'This block needs text.',
    'needs-bool': 'This slot needs a condition (true or false).',
    'compare-mismatch': 'You can only compare numbers with numbers and text with text.',
    'needs-array': (p: Record<string, string>) =>
      `${p.name ?? 'This'} is not a list — make the list first.`,
    'needs-index': 'A place number has to be a whole number.',
    'wrong-rank': (p: Record<string, string>) =>
      `${p.name ?? 'This'} is a table: give it a row and a column.`,
    'needs-char': 'This block needs one character, like “character i of s” — not a whole text.',
  },
  tutor: {
    title: 'Tutor',
    tagline: 'hints, not answers',
    subtitle: (title: string) => `${title} · explains, never solves`,
    explainTask: 'Explain the task',
    hint: 'Give me a hint',
    explainBlock: 'Explain this block',
    explainCode: 'Explain my code',
    explainThisCode: 'Explain this code',
    whyStop: 'Why did it stop?',
    askPlaceholder: 'Ask about your code…',
    send: 'Send',
    hintOf: (n: number) => `Hint ${n} of 3`,
    wasClear: 'Was this clear?',
    yes: 'Yes',
    notReally: 'Not really',
    thanks: 'Thanks!',
    footer: 'The tutor never gives full solutions. Your teacher can turn that on.',
    suggestions: ['What is a loop?', 'What does mod do?', 'What is a variable?'],
    thinking: 'Thinking…',
    unavailable: 'The tutor is not available right now. Try again in a minute.',
    rateLimited: 'That was a lot of questions — wait a few minutes and try again.',
    back: 'Back to the exercise',
    selectBlockFirst: 'Click a block first, then ask.',
    selected: (label: string) => `Selected: ${label} — asking the tutor`,
    noHintsLeft: 'That was the last hint. Try a few steps with Step and watch the variables.',
  },
  home: {
    pickUp: 'Pick up where you left off',
    continue: 'Continue',
    startOver: 'Start over',
    startOverConfirm: 'Start this exercise again from the beginning? Your blocks will be replaced.',
    editedAgo: (ago: string) => `Edited ${ago}`,
    lastRun: {
      finished: 'Last run finished',
      error: 'Last run stopped with an error',
      stepLimit: 'Last run stopped — loop never ends',
      stopped: 'Last run was stopped',
    },
    exercises: 'Exercises',
    moreIn: (n: number, cat: string) => `${n} more in ${cat}`,
    progress: (done: number, total: number) => `progress ${done} of ${total}`,
    joinTitle: 'Join a live session',
    joinText: 'Type the 6-letter code your teacher shows on screen.',
    joinPlaceholder: '7KQ3FZ',
    join: 'Join',
    joinInvalid: 'That code is not valid. Check it and try again.',
    codeShownAs: 'Code shown as',
    codeShownAsText:
      'Blocks stay the same. Pick the language you see beside them — switch any time.',
    myProjects: 'My projects',
    newProject: 'New',
    noProjects: 'Your free projects appear here.',
    delete: 'Delete',
    deleteConfirm: (title: string) => `Delete “${title}”? This cannot be undone.`,
    welcomeTitle: 'Learn to code with blocks — see real code',
    welcomeText:
      'Pick an exercise. Build the program with blocks and watch the same program appear in C++, pseudocod, Python or JavaScript.',
    start: 'Start',
  },
  share: {
    title: 'Share this session',
    text: 'Two links: one to watch, one to edit. Only share the edit link with the student.',
    watchLink: 'Watch link',
    viewOnly: 'view only',
    editLink: 'Edit link',
    canEdit: 'can change blocks',
    codeHint: 'Students can also type this code on the home page to watch.',
    creating: 'Starting the session…',
    failed: 'Could not start a session. Try again.',
  },
  live: {
    live: 'Live',
    watching: (name: string) => `You are watching ${name}'s workspace`,
    viewOnly: 'view only',
    youEdit: 'You are editing',
    session: 'session',
    startedAgo: (ago: string) => `Started ${ago}`,
    endSession: 'End session',
    endConfirm: 'End this session for everyone?',
    ended: 'This session has ended.',
    takeControl: 'Take control',
    askControl: 'Ask for control',
    controlAsked: 'Asked — waiting…',
    viewOnlyAsk: 'View only — ask for control to edit',
    giveControl: (name: string) => `${name} asks to edit`,
    allow: 'Allow',
    youHaveControl: 'You have control',
    activity: (name: string) => `${name}'s activity`,
    input: (name: string) => `${name}'s input`,
    lastRun: (name: string) => `${name}'s last run`,
    messageTo: (name: string) => `Message to ${name}`,
    messagePlaceholder: 'Try running it with 9 as input…',
    messageFrom: (name: string) => `Message from ${name}`,
    updatesLive: (name: string, ago: string) =>
      `Updates live as ${name} edits · last change ${ago}`,
    noActivity: 'Nothing yet.',
    reconnecting: 'Reconnecting…',
    you: 'You',
    participants: 'Participants',
    yourName: 'Your name',
    namePrompt: 'What should the others see as your name?',
    joinAs: 'Join',
    notFound: 'This session does not exist or has ended.',
    activityRun: (summary: string) => `Ran the program · ${summary}`,
    activityRunError: 'Ran the program · stopped with an error',
    activityRunLimit: 'Ran the program · loop never ended',
    activityTutor: (what: string) => `Asked the tutor: ${what}`,
    activityHint: (level: number) => `Asked the tutor for a hint (level ${level})`,
    activityBlock: (block: string) => `Added a ${block} block`,
    output: 'output',
  },
  rewards: {
    title: 'My progress',
    levelShort: 'Lv',
    chip: (level: number, points: number) => `Level ${level}, ${points} points — see your progress`,
    level: (n: number) => `Level ${n}`,
    points: (n: number) => `${n.toLocaleString('en')} points`,
    toNext: (n: number, level: number) => `${n} points to level ${level}`,
    streak: 'Streak',
    streakDays: (n: number) => (n === 1 ? '1 day' : `${n} days`),
    bestStreak: (n: number) => `Best: ${n === 1 ? '1 day' : `${n} days`}`,
    todayDone: 'Today counts ✓',
    todayOpen: 'Run a program today to keep it going',
    seeAll: 'See all rewards',
    badges: 'Badges',
    earned: (date: string) => `Earned ${date}`,
    unlockables: 'Unlockables',
    unlockablesText: 'Cosmetic only — every exercise is always open.',
    avatarColor: 'Avatar colour',
    avatarColorText: 'Your circle in live sessions.',
    icon: 'Profile icon',
    blockTheme: 'Block colours',
    equip: 'Use',
    equipped: 'In use',
    lockedLevel: (n: number) => `Level ${n}`,
    lockedBadge: (name: string) => `Badge: ${name}`,
    recent: 'Recent points',
    noRecent: 'Solve an exercise to earn your first points.',
    howToEarn: 'How to earn points',
    rules: {
      solve: 'Solve an exercise: 10, 20 or 30 points by difficulty.',
      noHint: 'Solve it without asking for hints: half as much again.',
      daily: 'Practise on a day: 5 points. Keep a streak for bonuses at 3, 7, 14 and 30 days.',
      learn:
        'Step through a program, look at the code in each language, take part in a live session: a few points more.',
      private: 'Only you see your points.',
    },
    toastPoints: (n: number, what: string) => `+${n} · ${what}`,
    toastBadge: (name: string) => `New badge: ${name}`,
    toastLevel: (n: number) => `Level ${n}! New things to unlock.`,
    event: {
      solve: (title: string) => `Solved ${title}`,
      'no-hint': (title: string) => `${title} without hints`,
      daily: 'Practised today',
      streak: (n: string) => `${n}-day streak`,
      step: 'Stepped through a program',
      lang: (lang: string) => `Code in ${lang}`,
      live: 'Live session',
    },
    badge: {
      'first-solve': ['First program', 'Solve your first exercise.'],
      'solver-10': ['Ten solved', 'Solve 10 exercises.'],
      'hard-one': ['Hard one', 'Solve a three-dot exercise.'],
      'no-hint-5': ['On my own', 'Solve 5 exercises without tutor hints.'],
      'category-sequences': ['Sequences master', 'Solve every Sequences exercise.'],
      'category-conditions': ['Decision maker', 'Solve every Conditions exercise.'],
      'category-loops': ['Loop master', 'Solve every Loops exercise.'],
      'category-digits': ['Number cruncher', 'Solve every Digits & numbers exercise.'],
      'category-arrays': ['Array ace', 'Solve every Vectors and matrices exercise.'],
      'category-strings': ['Word wizard', 'Solve every Text & characters exercise.'],
      'all-exercises': ['Catalog complete', 'Solve every exercise.'],
      'streak-3': ['3-day streak', 'Practise 3 days in a row.'],
      'streak-7': ['7-day streak', 'Practise 7 days in a row.'],
      'streak-30': ['30-day streak', 'Practise 30 days in a row.'],
      polyglot: ['Polyglot', 'Look at your code in all four languages.'],
      stepper: ['Step by step', 'Step through 10 programs.'],
      'live-learner': ['Live learner', 'Spend 5 minutes in a live session.'],
    } as Record<string, [string, string]>,
    colors: {
      clay: 'Clay',
      moss: 'Moss',
      sky: 'Sky',
      plum: 'Plum',
      rose: 'Rose',
      teal: 'Teal',
      amber: 'Amber',
      ink: 'Ink',
    } as Record<string, string>,
    icons: {
      pebble: 'Pebble',
      star: 'Star',
      bolt: 'Bolt',
      rocket: 'Rocket',
      crown: 'Crown',
      flame: 'Flame',
      brain: 'Brain',
      globe: 'Globe',
    } as Record<string, string>,
    themes: {
      classic: 'Classic',
      ocean: 'Ocean',
      forest: 'Forest',
      sunset: 'Sunset',
      neon: 'Neon',
    } as Record<string, string>,
  },
  time: {
    justNow: 'just now',
  },
  ...enAccounts,
};

export type Messages = typeof en;
